/**
 * Canonical 44-byte RIFF/WAVE PCM header + little-endian int16 samples, 16 kHz mono.
 * Sarvam auto-detects WAV, so the upload needs no codec hint; keep segments <= 25 s.
 */

export const WAV_HEADER_BYTES = 44;

const HOST_IS_LITTLE_ENDIAN = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;

export interface WavHeaderParams {
  sampleRate: number;
  channels: number;
  bitsPerSample: number;
  dataBytes: number;
}

/**
 * Build an audio/wav Blob from ordered Int16 frames (as posted by the worklet / Int16Framer).
 */
export function encodeWavFromInt16Frames(frames: readonly Int16Array[], sampleRate = 16000): Blob {
  let samples = 0;
  for (const f of frames) samples += f.length;
  const dataBytes = samples * 2; // 16-bit: always even, no pad byte
  const header = new ArrayBuffer(WAV_HEADER_BYTES);
  writeWavHeader(new DataView(header), { sampleRate, channels: 1, bitsPerSample: 16, dataBytes });
  if (HOST_IS_LITTLE_ENDIAN) {
    // Blob copies exactly the bytes each typed-array view covers; int16 in host order is LE on every phone.
    // (Cast: TS 5.9 types Int16Array over ArrayBufferLike, which is not assignable to BlobPart; our frames
    // are always plain ArrayBuffer-backed.)
    const parts: BlobPart[] = [header, ...(frames as unknown as BlobPart[])];
    return new Blob(parts, { type: "audio/wav" });
  }
  const body = new DataView(new ArrayBuffer(dataBytes)); // theoretical big-endian host
  let o = 0;
  for (const f of frames) for (let i = 0; i < f.length; i++, o += 2) body.setInt16(o, f[i], true);
  return new Blob([header, body.buffer], { type: "audio/wav" });
}

/** Write the 44-byte PCM header. All multi-byte fields little-endian. */
export function writeWavHeader(view: DataView, { sampleRate, channels, bitsPerSample, dataBytes }: WavHeaderParams): void {
  const blockAlign = (channels * bitsPerSample) >> 3; // 2
  const byteRate = sampleRate * blockAlign; // 32000
  putAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true); // file length - 8
  putAscii(view, 8, "WAVE");
  putAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size for PCM
  view.setUint16(20, 1, true); // wFormatTag 1 = PCM
  view.setUint16(22, channels, true); // 1
  view.setUint32(24, sampleRate, true); // 16000
  view.setUint32(28, byteRate, true); // 32000
  view.setUint16(32, blockAlign, true); // 2
  view.setUint16(34, bitsPerSample, true); // 16
  putAscii(view, 36, "data");
  view.setUint32(40, dataBytes, true);
}

function putAscii(view: DataView, off: number, s: string): void {
  for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
}

/** Duration in ms of a WAV produced by this encoder. */
export function wavDurationMs(blob: Blob, sampleRate = 16000): number {
  return ((blob.size - WAV_HEADER_BYTES) / 2 / sampleRate) * 1000;
}

/** Float32 [-1,1] -> Int16 with clamping (ScriptProcessorNode tier). */
export function floatToInt16(f32: Float32Array, out: Int16Array = new Int16Array(f32.length)): Int16Array {
  for (let i = 0; i < f32.length; i++) {
    let s = f32[i];
    if (s > 1) s = 1;
    else if (s < -1) s = -1;
    out[i] = Math.round(s * 32767);
  }
  return out;
}

/** Dev-build sanity check: parse the header back before upload. Throws on any mismatch. */
export async function assertWav(blob: Blob): Promise<void> {
  const v = new DataView(await blob.slice(0, WAV_HEADER_BYTES).arrayBuffer());
  const tag = (o: number) =>
    String.fromCharCode(v.getUint8(o), v.getUint8(o + 1), v.getUint8(o + 2), v.getUint8(o + 3));
  if (tag(0) !== "RIFF" || tag(8) !== "WAVE" || tag(12) !== "fmt " || tag(36) !== "data") throw new Error("bad WAV header");
  if (v.getUint16(20, true) !== 1 || v.getUint16(22, true) !== 1) throw new Error("bad WAV format");
  if (v.getUint32(24, true) !== 16000 || v.getUint16(34, true) !== 16) throw new Error("bad WAV rate/depth");
  if (v.getUint32(40, true) !== blob.size - WAV_HEADER_BYTES) throw new Error("bad WAV data size");
  if (v.getUint32(4, true) !== blob.size - 8) throw new Error("bad RIFF size");
}
